import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CheckSquare, Plus, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { CarteirasDialog } from '@/components/tasks/board/CarteirasDialog'
import { TaskBoardFilters } from '@/components/tasks/board/TaskBoardFilters'
import { TaskCalendar } from '@/components/tasks/board/TaskCalendar'
import { TaskList, type TaskGroup } from '@/components/tasks/board/TaskList'
import { TaskStageBoard } from '@/components/tasks/board/TaskStageBoard'
import { useAuth } from '@/contexts/AuthContext'
import { useTaskDrawer } from '@/contexts/TaskDrawerContext'
import { hasRole } from '@/lib/roles'
import {
  bandLabel, BoardFilters, boardFiltersFromSearch, boardFiltersToSearch, byBand, byResponsible, toPainelParams,
  type BoardView, type TaskStage, type TaskView, VIEWS,
} from '@/lib/tasks-board'
import { taskBoardService, type BoardPage, type Carteira } from '@/services/task-board-service'
import { apiErrorMessage, teamService, type TeamMember } from '@/services/team-service'
import * as toast from '@/lib/toast'

function groupsFor(view: BoardView, tasks: TaskView[]): TaskGroup[] {
  if (view === 'arquivo') return [{ key: 'arquivo', label: 'Arquivadas', tasks }]
  if (view === 'escritorio') {
    return byResponsible(tasks).map((g) => ({ key: g.key, label: g.key, tasks: g.tasks }))
  }
  return byBand(tasks).map((g) => ({ key: g.key, label: bandLabel(g.key), tasks: g.tasks }))
}

/** Prazos e tarefas: vistas do modelo Notion com filtros na URL; tudo abre na gaveta. */
export function TarefasPage() {
  const { user } = useAuth()
  const drawer = useTaskDrawer()
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => boardFiltersFromSearch(searchParams), [searchParams])
  const [page, setPage] = useState<BoardPage | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [members, setMembers] = useState<TeamMember[]>([])
  const [carteiras, setCarteiras] = useState<Carteira[]>([])
  const [showCarteiras, setShowCarteiras] = useState(false)

  const change = (patch: Partial<BoardFilters>) =>
    setSearchParams(boardFiltersToSearch({ ...filters, ...patch }, searchParams))

  const loadCarteiras = useCallback(() => {
    taskBoardService.listCarteiras().then(setCarteiras).catch(() => setCarteiras([]))
  }, [])

  useEffect(() => {
    teamService.listMembers().then((list) => setMembers(list.filter((m) => m.isActive))).catch(() => setMembers([]))
    loadCarteiras()
  }, [loadCarteiras])

  const painelKey = JSON.stringify(toPainelParams(filters))
  const load = useCallback(async () => {
    setLoading(true)
    try {
      setPage(await taskBoardService.painel(JSON.parse(painelKey)))
      setError(null)
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível carregar as tarefas'))
    } finally {
      setLoading(false)
    }
  }, [painelKey])

  useEffect(() => {
    load()
  }, [load, drawer.version])

  const open = (task: TaskView) => drawer.openTask(task.id)

  const move = async (task: TaskView, stage: TaskStage) => {
    if (stage === 'AGUARDANDO') {
      toast.info('Informe de quem, o quê e a data de retorno na tarefa')
      drawer.openTask(task.id)
      return
    }
    try {
      const result = await taskBoardService.moveStage(task.id, stage)
      if (result.enviada_para_revisao) toast.info('Tarefa com revisor: enviada para revisão')
      load()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Não foi possível mover a tarefa'))
    }
  }

  const month = filters.mes ?? (page?.hoje ?? new Date().toISOString()).slice(0, 7)
  const tasks = page?.tarefas ?? []

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CheckSquare className="size-6 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">Prazos e tarefas</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCarteiras(true)}><Users className="size-4" /> Carteiras</Button>
          <Button onClick={() => drawer.newTask()}><Plus className="size-4" /> Nova Tarefa</Button>
        </div>
      </div>

      <Tabs value={filters.vista} onValueChange={(vista) => change({ vista: vista as BoardView })}>
        <TabsList className="flex-wrap">
          {VIEWS.map((v) => <TabsTrigger key={v.value} value={v.value}>{v.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <TaskBoardFilters filters={filters} members={members} carteiras={carteiras} onChange={change} />
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      {page?.truncado && <p className="text-sm text-muted-foreground">Mostrando as primeiras 500 tarefas; refine os filtros.</p>}

      <Card>
        <CardContent>
          {loading && !page ? (
            <div className="flex flex-col gap-2"><Skeleton className="h-6" /><Skeleton className="h-24" /></div>
          ) : filters.vista === 'quadro' ? (
            <TaskStageBoard tasks={tasks} onOpen={open} onMove={move} />
          ) : filters.vista === 'calendario' ? (
            <TaskCalendar month={month} today={page?.hoje ?? ''} tasks={tasks} onOpen={open} onMonthChange={(mes) => change({ mes })} />
          ) : (
            <TaskList
              groups={groupsFor(filters.vista, tasks)}
              onOpen={open}
              showResponsible={filters.vista !== 'minha-semana'}
              emptyText={filters.vista === 'minha-semana' ? 'Nada pendente com você.' : 'Nenhuma tarefa com esses filtros.'}
            />
          )}
        </CardContent>
      </Card>

      <CarteirasDialog
        open={showCarteiras}
        onOpenChange={setShowCarteiras}
        carteiras={carteiras}
        members={members}
        canEdit={hasRole(user?.role, ['SOCIO'])}
        onChanged={loadCarteiras}
      />
    </div>
  )
}
